import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import loadingGif from './images/gif.gif';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Unauthorized } from './pages/Unauthorised';
import { initializeFirebase } from './firebase/config';
import { usePageTracking } from './hooks/usePageTracking';
import { useLocation } from 'react-router-dom';
import { City } from './types/trip';

// Keep only essential components as direct imports
import LoginPage from './pages/LoginPage';
import Homepage from './pages/Homepage';
import { AuthProvider } from './contexts/AuthContext';
import { TripProvider } from './contexts/TripContext';
import { ApiLimitProvider } from './contexts/ApiLimitContext';

// Define API_BASE_URL outside component to prevent re-renders
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://travelapp-bens.onrender.com' 
  : 'http://localhost:5001';

// Lazy load heavy components
const TripOverview = React.lazy(() => import('./pages/TripOverview'));
const AiAssist = React.lazy(() => import('./pages/AiAssist'));
const AccommodationDetailsPage = React.lazy(() => import('./pages/AccommodationDetailsPage'));
const Activities = React.lazy(() => import('./components/Activities'));
const AccommodationSearchPage = React.lazy(() => import('./pages/AccommodationSearchPage'));
const TransportPage = React.lazy(() => import('./pages/TransportPage'));
const InternalTransportPage = React.lazy(() => import('./pages/InternalTransportPage'));
const DiscoverPage = React.lazy(() => import('./pages/DiscoverPage'));
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const TestForm = React.lazy(() => import('./pages/TestForm'));
const BookingConfirm = React.lazy(() => import('./pages/BookingConfirm'));
const PaymentPage = React.lazy(() => import('./pages/PaymentPage'));
const BookingSuccess = React.lazy(() => import('./pages/BookingSuccess'));
const SharedTripPage = React.lazy(() => import('./pages/SharedTripPage'));

export const GoogleMapsContext = React.createContext<boolean>(false);

// Loading component for Suspense
const SuspenseLoader: React.FC = () => (
  <div className="fixed inset-0 bg-loading-gray flex justify-center items-center">
    <img src={loadingGif} alt="Loading..." className="!w-[230px] !h-[230px] !max-w-[230px] !max-h-[230px] object-contain" style={{width: '230px', height: '230px', maxWidth: '230px', maxHeight: '230px'}} />
  </div>
);

// Wrapper component for Activities to provide required props
const ActivitiesWrapper: React.FC = () => {
  const location = useLocation();
  
  // Default city if none provided
  const defaultCity: City = {
    id: "london",
    name: "London",
    country: "United Kingdom",
    coordinates: { lat: 51.5074, lng: -0.1278 }
  };

  // Get city from location state or use default
  const currentCity = location.state?.selectedCity || defaultCity;
  const allCities = location.state?.allCities || [currentCity];

  return (
    <Activities 
      currentCity={currentCity}
      isOpen={true}
      hideHeader={false}
      allCities={allCities}
    />
  );
};

const AppContent: React.FC = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://travelapp-bens.onrender.com' 
  : 'http://localhost:5001';

  usePageTracking();



  // Continuously check for Google Maps availability (fix for timing issues)
  useEffect(() => {
    if (!scriptLoaded && window.google?.maps) {
      console.log('🗺️ [App] Found Google Maps API already loaded, updating scriptLoaded state');
      setScriptLoaded(true);
    }
  }, [scriptLoaded]);

  // Additional check with interval to catch delayed loading
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (!scriptLoaded) {
      interval = setInterval(() => {
        if (window.google?.maps) {
          console.log('🗺️ [App] Google Maps API detected via interval check');
          setScriptLoaded(true);
        }
      }, 500);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [scriptLoaded]);

  // Global error handler for Google Analytics recursion issues
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      const error = event.error;
      if (error && error.message && error.message.includes('Maximum call stack size exceeded')) {
        console.warn('Detected call stack overflow, likely from Google Analytics. Disabling GA.');
        if (typeof window !== 'undefined') {
          (window as any).__GA_DISABLED__ = true;
        }
        event.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && typeof event.reason === 'string' && 
          event.reason.includes('Maximum call stack size exceeded')) {
        console.warn('Detected promise rejection with call stack overflow. Disabling GA.');
        if (typeof window !== 'undefined') {
          (window as any).__GA_DISABLED__ = true;
        }
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Initialize everything in parallel and show app quickly
  useEffect(() => {
    let isMounted = true;

    const initializeApp = async () => {
      // Show app immediately - don't wait for anything
      if (isMounted) setAppReady(true);

      // Start everything in parallel (non-blocking)
      const initPromises = [
        // Firebase initialization
        initializeFirebase().then(() => {
          if (isMounted) setIsInitialized(true);
        }).catch(error => {
          console.error('Failed to initialize Firebase:', error);
          if (isMounted) setIsInitialized(true);
        }),

        // Google Maps API loading - Load in background without blocking UI
        (async () => {
          // Check if already loaded or loading
          if (window.google?.maps || window.__GOOGLE_MAPS_LOADING__) {
            if (window.google?.maps && isMounted) setScriptLoaded(true);
            return;
          }

          console.log('🗺️ Loading Google Maps API in background...');
          window.__GOOGLE_MAPS_LOADING__ = true;

          try {
            // Add timeout to prevent hanging indefinitely
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const response = await fetch(`${API_BASE_URL}/api/maps/token`, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Accept': 'application/json' },
              signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            if (!data.token) throw new Error('No API key received');

            // Create script with async loading
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${data.token}&libraries=places&loading=async`;
            script.async = true;
            script.onload = () => {
              console.log('🗺️ Google Maps API loaded successfully');
              window.__GOOGLE_MAPS_LOADING__ = false;
              if (isMounted) setScriptLoaded(true);
            };
            script.onerror = () => {
              console.error('🗺️ Failed to load Google Maps script');
              window.__GOOGLE_MAPS_LOADING__ = false;
              if (isMounted) setScriptLoaded(false);
            };
            document.head.appendChild(script);

          } catch (error) {
            console.error('Error loading Google Maps API:', error);
            window.__GOOGLE_MAPS_LOADING__ = false;
            if (isMounted) setScriptLoaded(false);
          }
        })(),

        // CSRF token (non-blocking)
        fetch('/api/csrf-token', {
          method: 'GET',
          credentials: 'include',
        }).catch(error => console.error('CSRF token error:', error))
      ];

      await Promise.allSettled(initPromises);
    };

    initializeApp();

    return () => {
      isMounted = false;
    };
  }, [API_BASE_URL]);

  // Only show loading screen briefly for initial app startup
  if (!appReady) {
    return (
      <div className="fixed inset-0 bg-loading-gray flex justify-center items-center">
        <img src={loadingGif} alt="Loading..." className="!w-[230px] !h-[230px] !max-w-[230px] !max-h-[230px] object-contain" style={{width: '230px', height: '230px', maxWidth: '230px', maxHeight: '230px'}} />
      </div>
    );
  }

  return (
    <GoogleMapsContext.Provider value={scriptLoaded}>
      <div className="min-h-screen bg-gray-50 flex flex-col font-montserrat">
        <Header />
        <div className="flex-1">
          <Suspense fallback={<SuspenseLoader />}>
            <Routes>
              <Route path="/" element={<Homepage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/test" element={<TestForm />} />
              <Route path="/ai-assist" element={<AiAssist />} />
              <Route path="/activities" element={<ActivitiesWrapper />} />
              <Route path="/discover/:cityParam" element={<DiscoverPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/accommodations/:accommodationId" element={<AccommodationDetailsPage />} />
              <Route path="/accommodation-search" element={<AccommodationSearchPage />} />
              <Route path="/transport" element={<TransportPage />} />
              <Route path="/internal-transport" element={<InternalTransportPage />} />
              <Route path="/trip-overview/:tripId?" element={
                <ProtectedRoute>
                  <TripOverview isScriptLoaded={scriptLoaded} />
                </ProtectedRoute>
              } />
              <Route path="/trip-overview/:tripId/booking" element={
                <ProtectedRoute>
                  <BookingConfirm />
                </ProtectedRoute>
              } />
              <Route path="/trip-overview/:tripId/payment" element={
                <ProtectedRoute>
                  <PaymentPage />
                </ProtectedRoute>
              } />
              <Route path="/trip-overview/:tripId/booking-success" element={
                <ProtectedRoute>
                  <BookingSuccess />
                </ProtectedRoute>
              } />
              <Route path="/shared/:token" element={<DiscoverPage />} />
              <Route path="/shared-trip" element={<SharedTripPage />} />
              <Route path="/account" element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              } />
            </Routes>
          </Suspense>
        </div>
        <Footer />
      </div>
    </GoogleMapsContext.Provider>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ApiLimitProvider>
        <TripProvider>
          <Router>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          </Router>
        </TripProvider>
      </ApiLimitProvider>
    </AuthProvider>
  );
};

export default App;